import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Headphones, Palette, Zap, ChevronDown, Building2, UtensilsCrossed, Home, Calendar, ShoppingBag, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { categoriesAPI, productsAPI, publicAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const iconMap = {
  Building2,
  UtensilsCrossed,
  Home,
  Calendar,
  ShoppingBag,
  Briefcase,
};

export function HomePage() {
  const { t, language } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [useCases, setUseCases] = useState([]);
  const [faq, setFaq] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catRes, prodRes, ucRes, faqRes] = await Promise.all([
        categoriesAPI.getAll(),
        productsAPI.getFeatured(),
        publicAPI.getUseCases(),
        publicAPI.getFAQ()
      ]);
      setCategories(catRes.data || []);
      setFeaturedProducts(prodRes.data?.products || []);
      setUseCases(ucRes.data || []);
      setFaq(faqRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const trustItems = [
    { icon: Shield, title: t('trust.quality'), desc: t('trust.quality.desc') },
    { icon: Headphones, title: t('trust.support'), desc: t('trust.support.desc') },
    { icon: Palette, title: t('trust.custom'), desc: t('trust.custom.desc') },
    { icon: Zap, title: t('trust.fast'), desc: t('trust.fast.desc') },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-white noise-bg overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient Orbs */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-[#DC2626]/20 to-[#EF4444]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-gradient-to-r from-[#991B1B]/10 to-[#B91C1C]/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-gradient-to-r from-[#DC2626]/15 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-[10%] w-4 h-4 bg-[#DC2626] rounded-full animate-float opacity-60"></div>
          <div className="absolute top-32 right-[15%] w-6 h-6 bg-[#991B1B]/30 rotate-45 animate-float-slow"></div>
          <div className="absolute top-40 left-[30%] w-3 h-3 bg-[#DC2626]/50 rounded-full animate-float-reverse"></div>
          <div className="absolute bottom-32 right-[25%] w-5 h-5 bg-[#DC2626]/40 rounded-full animate-float"></div>
          <div className="absolute bottom-40 left-[20%] w-4 h-4 bg-[#991B1B]/20 rotate-45 animate-float-slow"></div>
          <div className="absolute top-1/2 right-[10%] w-3 h-3 bg-[#DC2626] rounded-full animate-float-reverse opacity-50"></div>
          
          {/* Animated Lines */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#DC2626" stopOpacity="0" />
                <stop offset="50%" stopColor="#DC2626" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#DC2626" stopOpacity="0" />
              </linearGradient>
            </defs>
            <line x1="0" y1="30%" x2="100%" y2="30%" stroke="url(#lineGradient)" strokeWidth="1" className="animate-line-move" />
            <line x1="0" y1="70%" x2="100%" y2="70%" stroke="url(#lineGradient)" strokeWidth="1" className="animate-line-move-reverse" />
          </svg>
          
          {/* NFC Wave Animation */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-32 h-32 border-2 border-[#DC2626]/20 rounded-full animate-ping-slow"></div>
            <div className="absolute inset-0 w-32 h-32 border-2 border-[#DC2626]/15 rounded-full animate-ping-slow" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute inset-0 w-32 h-32 border-2 border-[#DC2626]/10 rounded-full animate-ping-slow" style={{animationDelay: '1s'}}></div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 relative z-10">
          {/* Centered Title */}
          <div className="text-center mb-8 animate-slide-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight font-['Outfit']">
              {t('hero.title')}
            </h1>
          </div>
          
          {/* Hero Banner Image */}
          <div className="mb-12 animate-slide-up" style={{ animationDelay: '0.2s' }} data-testid="hero-banner">
            <div className="relative overflow-hidden rounded-2xl shadow-xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/19eba2b9-5313-4906-8ba3-c39d938097d6/images/f16530b6ebd4cfa9593c555c707654a42dce6dd10185c74e562b252c5239c799.png"
                alt="Produits NFC Logotag - Transmettez toutes vos informations en 1 seconde"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
          
          {/* Subtitle - Centered with line breaks */}
          <div className="text-center max-w-4xl mx-auto mb-12 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              <span className="block font-semibold text-foreground mb-2">{t('hero.subtitle.line1')}</span>
              <span className="block mb-1">{t('hero.subtitle.line2')}</span>
              <span className="block mb-2">{t('hero.subtitle.line3')}</span>
              <span className="block">{t('hero.subtitle.line4')}</span>
            </p>
          </div>
          
          {/* CTA Buttons - Centered */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <Link to="/devis">
              <Button
                size="lg"
                className="bg-[#DC2626] hover:bg-[#B91C1C] rounded-none uppercase tracking-widest text-xs px-8 py-6 h-auto text-white"
                data-testid="hero-quote-btn"
              >
                {t('hero.cta.quote')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/catalogue">
              <Button
                variant="outline"
                size="lg"
                className="rounded-none uppercase tracking-widest text-xs px-8 py-6 h-auto border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                data-testid="hero-catalog-btn"
              >
                {t('hero.cta.catalog')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section - Grid symétrique */}
      <section className="py-20 md:py-28 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">
              {t('categories.title')}
            </h2>
          </div>
          
          {/* Grid 4 colonnes symétrique - 2 lignes de 4 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 7).map((category, index) => (
              <Link
                key={category.category_id}
                to={`/catalogue/${category.slug}`}
                className="group relative overflow-hidden rounded-2xl animate-slide-up"
                style={{ animationDelay: `${index * 0.08}s` }}
                data-testid={`category-${category.slug}`}
              >
                <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                  {/* Image */}
                  {category.image && (
                    <img
                      src={category.image}
                      alt={language === 'fr' ? category.name_fr : category.name_en}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  )}
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-300"></div>
                  
                  {/* Animated border on hover */}
                  <div className="absolute inset-0 border-4 border-transparent group-hover:border-[#DC2626] rounded-2xl transition-all duration-300"></div>
                  
                  {/* Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-bold text-white text-base md:text-lg leading-tight">
                      {language === 'fr' ? category.name_fr : category.name_en}
                    </h3>
                    
                    {/* Arrow indicator with animation */}
                    <div className="mt-2 flex items-center gap-2 text-white/70 group-hover:text-[#DC2626] transition-all duration-300">
                      <span className="text-xs font-medium uppercase tracking-wider">Découvrir</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </div>
                  
                  {/* Shine effect on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </div>
                </div>
              </Link>
            ))}
            
            {/* 8ème carte - Voir tout le catalogue */}
            <Link
              to="/catalogue"
              className="group relative overflow-hidden rounded-2xl animate-slide-up"
              style={{ animationDelay: `0.56s` }}
              data-testid="category-all"
            >
              <div className="relative aspect-square bg-[#DC2626] overflow-hidden flex items-center justify-center">
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-4 left-4 w-8 h-8 border-2 border-white rounded-full animate-ping-slow"></div>
                  <div className="absolute bottom-8 right-8 w-6 h-6 border-2 border-white rounded-full animate-ping-slow" style={{animationDelay: '0.5s'}}></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 border-2 border-white rounded-full animate-ping-slow" style={{animationDelay: '1s'}}></div>
                </div>
                
                {/* Content */}
                <div className="relative text-center p-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <ArrowRight className="w-8 h-8 text-white group-hover:translate-x-1 transition-transform" />
                  </div>
                  <h3 className="font-bold text-white text-lg md:text-xl leading-tight">
                    Voir tout le catalogue
                  </h3>
                  <p className="mt-2 text-white/80 text-sm">
                    77 produits NFC
                  </p>
                </div>
                
                {/* Hover border */}
                <div className="absolute inset-0 border-4 border-transparent group-hover:border-white rounded-2xl transition-all duration-300"></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="usecases-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">
              {t('usecases.title')}
            </h2>
            <p className="text-muted-foreground">
              {t('usecases.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {useCases.map((useCase, index) => {
              const IconComponent = iconMap[useCase.icon] || Building2;
              return (
                <Link
                  key={useCase.usecase_id}
                  to={`/cas-usage/${useCase.slug}`}
                  className="group animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`usecase-${useCase.slug}`}
                >
                  <div className="p-6 bg-white border border-border hover:border-[#DC2626] transition-all duration-300 text-center">
                    <IconComponent className="h-8 w-8 mx-auto mb-4 text-muted-foreground group-hover:text-[#DC2626] transition-colors" />
                    <h3 className="text-sm font-medium">
                      {language === 'fr' ? useCase.title_fr : useCase.title_en}
                    </h3>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {trustItems.map((item, index) => (
              <div
                key={index}
                className="text-center animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center border border-border">
                  <item.icon className="h-6 w-6 text-[#DC2626]" />
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-20 md:py-28 bg-gray-50" data-testid="featured-products-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between mb-12">
              <h2 className="text-3xl md:text-4xl font-bold font-['Outfit']">
                {language === 'fr' ? 'Produits phares' : 'Featured Products'}
              </h2>
              <Link to="/catalogue">
                <Button variant="ghost" className="group">
                  {t('categories.viewAll')}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.slice(0, 3).map((product, index) => (
                <Link
                  key={product.product_id}
                  to={`/produit/${product.slug}`}
                  className="group product-card bg-white border border-border animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`featured-product-${product.slug}`}
                >
                  <div className="aspect-square bg-secondary overflow-hidden">
                    {product.images?.[0] && (
                      <img
                        src={product.images[0]}
                        alt={language === 'fr' ? product.name_fr : product.name_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold mb-2">
                      {language === 'fr' ? product.name_fr : product.name_en}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {language === 'fr' ? product.description_fr : product.description_en}
                    </p>
                    <span className="product-cta inline-flex items-center text-sm font-medium text-[#DC2626]">
                      {t('products.requestQuote')}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faq.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center font-['Outfit']">
              {t('faq.title')}
            </h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faq.map((item, index) => (
                <AccordionItem
                  key={item.faq_id}
                  value={item.faq_id}
                  className="border border-border px-6 data-[state=open]:border-[#DC2626] transition-colors"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-medium">
                      {language === 'fr' ? item.question_fr : item.question_en}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-4">
                    {language === 'fr' ? item.answer_fr : item.answer_en}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#DC2626] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-['Outfit']">
            {language === 'fr' ? 'Prêt à démarrer votre projet NFC ?' : 'Ready to start your NFC project?'}
          </h2>
          <p className="text-red-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Contactez-nous pour discuter de vos besoins et obtenir un devis personnalisé.'
              : 'Contact us to discuss your needs and get a personalized quote.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button
                size="lg"
                className="bg-white hover:bg-gray-100 text-[#DC2626] rounded-none uppercase tracking-widest text-xs px-8 py-6 h-auto"
                data-testid="cta-quote-btn"
              >
                {t('hero.cta.quote')}
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                size="lg"
                className="rounded-none uppercase tracking-widest text-xs px-8 py-6 h-auto border-white text-white hover:bg-white hover:text-[#DC2626]"
                data-testid="cta-contact-btn"
              >
                {t('footer.contact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
